import { observer } from 'mobx-react-lite';
import { createContext, ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import { useWorkspacePanel } from '../../../panels/hooks/use-workspace-panel';
import {
    ActionHandler,
    useIntegratedActionBroadcaster,
} from '../../../action-broadcaster/use-integrated-action-broadcaster';
import { BoloPanelTab } from '../config/bolo-panel-tabs-config';

export type SetActiveBoloPanelTab = React.Dispatch<React.SetStateAction<BoloPanelTab | undefined>>;

const ActiveBoloPanelTabContext = createContext<BoloPanelTab | undefined>(undefined);
const SetActiveBoloPanelTabContext = createContext<undefined | SetActiveBoloPanelTab>(undefined);

export const ActiveBoloPanelTabProvider = observer(function ActiveBoloPanelTabProvider(props: {
    children: ReactElement;
}) {
    const workspacePanel = useWorkspacePanel<'BOLO'>();
    const [activeBoloPanelTab, setActiveBoloPanelTab] = useState<BoloPanelTab | undefined>();
    const workspacePanelUuid = workspacePanel.uuid;
    const boloId = workspacePanel.linkedEntityId;
    const [defaultActiveTabHasBeenSet, setDefaultActiveTabHasBeenSet] = useState(false);

    useEffect(function setDefaultActiveTab() {
        setActiveBoloPanelTab('RELATED_DATA');
        setDefaultActiveTabHasBeenSet(true);
    }, []);

    const handleActions: ActionHandler<'EVENT_V2'> = useCallback(
        (action) => {
            switch (action.type) {
                case 'SET_BOLO_PANEL_TAB': {
                    if (
                        ('workspacePanelClientUuid' in action.payload &&
                            action.payload.workspacePanelClientUuid === workspacePanelUuid) ||
                        ('boloId' in action.payload && action.payload.boloId === boloId)
                    ) {
                        setActiveBoloPanelTab(action.payload.tab);
                        return true;
                    }
                    break;
                }
            }
        },
        [setActiveBoloPanelTab, workspacePanelUuid, boloId]
    );

    useIntegratedActionBroadcaster({
        id: 'BOLO_PANEL_TABS',
        windowType: 'EVENT_V2',
        /**
         * Only after the default has been set, do we start listening
         * for requests to change the tab
         *
         * Otherwise, it would be possible for the `default` to override
         * the request
         */
        handler: defaultActiveTabHasBeenSet ? handleActions : undefined,
    });

    return (
        <ActiveBoloPanelTabContext.Provider value={activeBoloPanelTab}>
            <SetActiveBoloPanelTabContext.Provider value={setActiveBoloPanelTab}>
                {props.children}
            </SetActiveBoloPanelTabContext.Provider>
        </ActiveBoloPanelTabContext.Provider>
    );
});

export function useActiveBoloPanelTab() {
    return useContext(ActiveBoloPanelTabContext);
}

export function useSetActiveBoloPanelTab() {
    const value = useContext(SetActiveBoloPanelTabContext);
    if (!value)
        throw new Error('Unexpectedly did not find active event panel tab setter on context');
    return value;
}
